import React, { useEffect, useMemo, useReducer } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import TextInput from "../../components/Common/TextInput";
import { useAccounts } from "../../DataStore/AccountsContext";
import { useTag } from "../../DataStore/TagContext";
import { useTheme } from "../../DataStore/ThemeContext";
import Notification from "../../components/Common/Notification";
import ajaxExpenses from "../../util/remote/ajaxExpenses";
import { useRefreshTrans } from "../../DataStore/useRefreshTrans";
import VendorSelector from "../../components/Transaction/VendorSelector";
import SectionTitle from "../../components/Common/SectionTitle";
import AccountingHint from "../../components/Ledger/AccountingHint";

const initialState = {
  loading: false,

  expenseAccount: null, // option object
  amount: "",
  description: "",
  transactionRef: "",
  transactionDate: "",

  payee: null, // vendor option object
  tag: null,   // option object
};

const reducer = (state, action) => {
  switch (action.type) {
    case "RESET_PARTIAL":
      return { ...state, amount: "", description: "" };
    default:
      return { ...state, [action.type]: action.value };
  }
};

// 🔹 helper: consistent responsive columns (1 / 2 / 3 per row)
const Col = ({ children }) => <div className="col-12 col-md-6 col-xl-4">{children}</div>;

const NewSupplierInvoicePage = () => {
  const { openSettings, setNotify, currentNotify } = useTheme();
  const refreshBalances = useRefreshTrans();
  const [state, dispatch] = useReducer(reducer, initialState);

  const { getExpenseAccounts, expenseAccountListing } = useAccounts();
  const { getTagListing, tagListing } = useTag();

  useEffect(() => {
    getExpenseAccounts();
    getTagListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expenseOptions = useMemo(() => {
    const opts = [];
    if (Array.isArray(expenseAccountListing)) {
      expenseAccountListing.forEach((a) =>
        opts.push({ label: `${a.account_number} ${a.account_name}`, value: a.account_id })
      );
    }
    return opts;
  }, [expenseAccountListing]);

  const tagOptions = useMemo(() => {
    const opts = [
      { label: "Create New Project...", value: "add" },
      { label: "- No project -", value: "0" },
    ];
    if (Array.isArray(tagListing)) {
      tagListing.forEach((t) => opts.push({ label: t.tag_name, value: t.tag_id }));
    }
    return opts;
  }, [tagListing]);

  const invoiceHint = useMemo(() => {
    const exp = state.expenseAccount?.label || "Expense Category";
    const vendor = state.payee?.label || "Vendor/Supplier";
    return `Supplier invoice journal: Debit ${exp}, Credit Accounts Payable (${vendor}). Payment later will Debit Accounts Payable and Credit Cash/Bank.`;
  }, [state.expenseAccount, state.payee]);

  const onConfirm = async (event) => {
    event.preventDefault();

    if (Number(state.amount) <= 0) {
      setNotify(<Notification type="danger" message="Enter invoice amount and try again." />);
      return;
    }

    if (!state.expenseAccount?.value) {
      setNotify(<Notification type="danger" message="Select expense category and try again." />);
      return;
    }

    if (!state.payee?.value) {
      setNotify(<Notification type="danger" message="Select vendor/supplier - i.e. creditor." />);
      return;
    }

    dispatch({ type: "loading", value: true });

    const server_response = await ajaxExpenses.createInvoice({
      invoice_ref: state.transactionRef,
      vendor_id: state.payee.value,
      invoice_date: state.transactionDate,
      amount: state.amount,
      description: state.description,
      expense_account: state.expenseAccount.value,
      tag: state.tag?.value || "0",
    });

    dispatch({ type: "loading", value: false });

    if (server_response.status === "OK") {
      dispatch({ type: "RESET_PARTIAL" });
      setNotify(<Notification type="success" message={server_response.message} />);
      refreshBalances();
    } else {
      setNotify(<Notification type="danger" message={server_response.message} />);
    }
  };

  return (
    <PageContainer>
      <MainContent>
        <PageTitle
          title="New Supplier Invoice"
          description="Enter incoming invoices from suppliers. These will be posted to Accounts Payable and cleared upon payment."
          iconClass="fa fa-upload"
        />

        <div className="mb-3">
          <AccountingHint hint={invoiceHint} />
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            {currentNotify}

            <form method="post" onSubmit={onConfirm}>
              {/* ---------- INVOICE DETAILS ---------- */}
              <div className="row mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-file-invoice"
                    title="Invoice Details"
                    note="Capture supplier invoice details for payables tracking."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <Col>
                  <TextInput
                    type="date"
                    label="Invoice Date"
                    placeholder="Invoice Date"
                    leftIcon="fa fa-calendar-alt"
                    value={state.transactionDate}
                    onChange={(e) => dispatch({ type: "transactionDate", value: e.target.value })}
                    required
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="text"
                    label="Invoice Ref"
                    placeholder="Invoice Reference/Invoice No."
                    leftIcon="fa fa-hashtag"
                    value={state.transactionRef}
                    onChange={(e) => dispatch({ type: "transactionRef", value: e.target.value })}
                    required
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="number"
                    label="Invoice Amount"
                    placeholder="Invoice Amount"
                    leftIcon="fa fa-credit-card"
                    value={state.amount}
                    onChange={(e) => dispatch({ type: "amount", value: e.target.value })}
                    required
                    wrap
                  />
                </Col>
              </div>

              {/* ---------- DESCRIPTION ---------- */}
              <div className="row mt-3 mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-list"
                    title="Description"
                    note="Describe the service/product on the invoice."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <div className="col-12">
                  <TextInput
                    type="textarea"
                    label="Invoice Description"
                    placeholder="Describe service/product on the invoice."
                    leftIcon="fa fa-list"
                    value={state.description}
                    onChange={(e) => dispatch({ type: "description", value: e.target.value })}
                    required
                    wrap
                  />
                </div>
              </div>

              {/* ---------- CLASSIFICATION ---------- */}
              <div className="row mt-3 mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-tag"
                    title="Classification"
                    note="Select expense category and optionally attach a project."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <Col>
                  <TextInput
                    type="select"
                    label="Expense Category"
                    placeholder="Select expense account..."
                    leftIcon="fa fa-university"
                    options={expenseOptions}
                    onChange={(item) => dispatch({ type: "expenseAccount", value: item })}
                    leftComponent={<i className="fa fa-sync link" onClick={() => getExpenseAccounts(true)} />}
                    loading={!expenseAccountListing}
                    value={state.expenseAccount}
                    wrap
                  />
                </Col>

                <Col>
                  <VendorSelector
                    onChange={(item) => {
                      if (item?.value === "add") {
                        openSettings("Create New Vendor", "NewVendor");
                        return;
                      }
                      dispatch({ type: "payee", value: item });
                    }}
                    placeholder="Select vendor e.g. supplier, National Water etc..."
                    label="Vendor/Supplier"
                    value={state.payee}
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="select"
                    label="Tag Transaction"
                    placeholder={state.tag?.label || "Transaction is not tagged..."}
                    leftIcon="fa fa-tag"
                    options={tagOptions}
                    onChange={(item) => {
                      if (item?.value === "add") {
                        openSettings("Create New Project", "NewTag", null, "Create Project");
                        return;
                      }
                      dispatch({ type: "tag", value: item });
                    }}
                    leftComponent={<i className="fa fa-sync link" onClick={() => getTagListing(true)} />}
                    loading={!tagListing}
                    value={state.tag}
                    wrap
                  />
                </Col>
              </div>

              {/* Actions */}
              <div className="d-flex flex-column flex-md-row justify-content-end mt-3" style={{ gap: 10 }}>
                <button type="submit" className="btn btn-primary btn-lg" disabled={state.loading}>
                  {state.loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-check-circle mr-2" />
                      Save Invoice
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-light btn-lg"
                  onClick={() => dispatch({ type: "RESET_PARTIAL" })}
                  disabled={state.loading}
                >
                  <i className="fa fa-undo mr-2" />
                  Clear Amount & Note
                </button>
              </div>

              <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                Tip: Supplier invoices increase Accounts Payable until you make payment.
              </div>
            </form>
          </div>
        </div>
      </MainContent>
    </PageContainer>
  );
};

export default NewSupplierInvoicePage;
